import { MedicationEntryType, MedicationRequirementStatus, MedicationRoute } from '../../types';
import { formatCivilDate, formatDublinDateTime } from '../../utils/date';

/**
 * Human labels shared by the two inspector-facing surfaces — the location
 * register and the print record.
 *
 * Deliberately its own module rather than a re-export from
 * ParentChildMedications: the parent screens speak to families ("Awaiting your
 * consent", "By mouth"), and this record speaks to an inspector, who needs the
 * status the system actually holds. The two vocabularies are allowed to differ,
 * and pointing one at the other would couple a statutory document to the
 * wording of a family screen.
 */
export const STATUS_LABELS: Record<MedicationRequirementStatus, string> = {
  [MedicationRequirementStatus.PENDING_CONSENT]: 'Awaiting consent',
  [MedicationRequirementStatus.ACTIVE]: 'Active',
  [MedicationRequirementStatus.ENDED]: 'Ended',
  [MedicationRequirementStatus.REVOKED]: 'Consent withdrawn',
  [MedicationRequirementStatus.EXPIRED_MEDICATION]: 'Medication expired',
};

export const ROUTE_LABELS: Record<MedicationRoute, string> = {
  [MedicationRoute.ORAL]: 'Oral',
  [MedicationRoute.INHALED]: 'Inhaled',
  [MedicationRoute.TOPICAL]: 'Topical',
  [MedicationRoute.INJECTION]: 'Injection',
  [MedicationRoute.OTHER]: 'Other',
};

export const ENTRY_TYPE_LABELS: Record<MedicationEntryType, string> = {
  [MedicationEntryType.ADMINISTERED]: 'Given',
  [MedicationEntryType.REFUSED]: 'Refused by child',
  [MedicationEntryType.MISSED]: 'Missed',
  [MedicationEntryType.PARTIAL]: 'Partial dose',
  [MedicationEntryType.CORRECTION]: 'Correction',
  [MedicationEntryType.EMERGENCY_USE]: 'Emergency use',
};

export const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * A plain `YYYY-MM-DD` for display. Delegates to formatCivilDate so a bare
 * date string is never read as UTC midnight — that renders as the PREVIOUS
 * day west of Greenwich, which on a medication record would misdate a
 * course's start.
 */
export function formatDate(iso: string): string {
  return formatCivilDate(iso) || iso;
}

/** A full timestamp — the year included, because a printed record outlives the
 *  year it was printed in. */
export function formatDateTime(iso: string): string {
  return formatDublinDateTime(iso);
}

/** The viewer-local `YYYY-MM-DD` `n` days before today, for the date inputs. */
export function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toIsoDate(d);
}

export function toIsoDate(d: Date): string {
  // Local components, not toISOString(): after 00:00 local but before UTC
  // midnight the UTC date is still yesterday, which would default the print
  // range to a day earlier than the operator sees on their own calendar.
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}
